import { createContext, useContext, useState, useCallback } from "react";
import { API } from "@/App";
import { toast } from "sonner";

const CompareContext = createContext(null);

export const MAX_COMPARE_ITEMS = 3;

/**
 * Phase 93: Smart Alias - Simplify complex asset names for better clinical data lookup
 * Extracts core drug/molecule name from long intervention titles
 */
export function simplifyAssetName(name) {
  if (!name || typeof name !== 'string') return { original: name, search: name, isSimplified: false };
  
  const original = name.trim();
  let search = original;
  
  // Patterns to strip from the name
  const stripPatterns = [
    /\s+in\s+combination\s+with\s+.*/i,
    /\s+combined\s+with\s+.*/i,
    /\s+plus\s+.*/i,
    /\s+\+\s+.*/i,
    /\s+and\s+a\s+healthy\s+lifestyle\s+.*/i,
    /\s+for\s+treatment\s+of\s+.*/i,
    /\s+for\s+the\s+treatment\s+of\s+.*/i,
    /\s+versus\s+.*/i,
    /\s+vs\.?\s+.*/i,
    /\s+compared\s+to\s+.*/i,
    /\s+administered\s+.*/i,
    /\s+given\s+.*/i,
    /\s+with\s+or\s+without\s+.*/i,
    /\s*\([^)]*\)\s*/g,  // Remove parenthetical content
  ];
  
  // Prefixes that can be stripped to get to the molecule name
  const prefixPatterns = [
    /^oral\s+/i,
    /^injectable\s+/i,
    /^subcutaneous\s+/i,
    /^intravenous\s+/i,
    /^once\s+weekly\s+/i,
    /^once\s+daily\s+/i,
    /^low\s+dose\s+/i,
    /^high\s+dose\s+/i,
  ];
  
  // Apply strip patterns
  for (const pattern of stripPatterns) {
    search = search.replace(pattern, '');
  }
  
  // Apply prefix patterns
  for (const pattern of prefixPatterns) {
    search = search.replace(pattern, '');
  }
  
  // Clean up whitespace
  search = search.trim().replace(/\s+/g, ' ');
  
  // If the name is still very long (>40 chars), try to get first 2-3 words
  if (search.length > 40) {
    const words = search.split(' ').filter(w => w.length > 1);
    search = words.slice(0, 2).join(' ');
  }
  
  const isSimplified = search.toLowerCase() !== original.toLowerCase() && search.length < original.length;
  
  return {
    original,
    search: search || original,
    isSimplified
  };
}

export function CompareProvider({ children }) {
  const [compareItems, setCompareItems] = useState([]);
  const [compareData, setCompareData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Phase 93: Add item with Smart Alias support
  const addToCompare = useCallback((item) => {
    setCompareItems((prev) => {
      // Check if already exists (by original name)
      const originalName = item.original || item.query;
      if (prev.some((i) => (i.original || i.query).toLowerCase() === originalName.toLowerCase())) {
        return prev;
      }
      // Max 3 items
      if (prev.length >= MAX_COMPARE_ITEMS) {
        return prev;
      }
      
      // Phase 27: Like-to-Like comparison enforcement
      if (prev.length > 0 && item.entityType) {
        const existingEntityType = prev[0].entityType;
        if (existingEntityType && existingEntityType !== item.entityType) {
          toast.error(
            `Cannot compare ${item.entityType}s with ${existingEntityType}s. ` +
            `Clear compare to switch entity types.`
          );
          return prev;
        }
      }
      
      // Phase 93: Apply Smart Alias if not already processed
      let newItem = { ...item };
      if (!item.search) {
        const alias = simplifyAssetName(item.query);
        newItem = {
          ...item,
          query: alias.search,  // Use simplified for API calls
          original: alias.original,
          search: alias.search,
          isSimplified: alias.isSimplified,
          entityType: item.entityType || null
        };
        
        if (alias.isSimplified) {
          toast.info(
            `Smart Search: Using "${alias.search}" for better results`,
            { duration: 3000 }
          );
        }
      }
      
      return [...prev, newItem];
    });
  }, []);

  // Phase 93: Revert to original name (use full name for search)
  const revertToOriginal = useCallback((originalName) => {
    setCompareItems((prev) => 
      prev.map((item) => {
        if (item.original?.toLowerCase() === originalName.toLowerCase()) {
          return {
            ...item,
            query: item.original,
            search: item.original,
            isSimplified: false
          };
        }
        return item;
      })
    );
    // Clear cached data to force re-fetch with original name
    setCompareData({});
    toast.info("Using original name for search");
  }, []);

  const removeFromCompare = useCallback((query) => {
    setCompareItems((prev) => prev.filter((i) => 
      i.query.toLowerCase() !== query.toLowerCase() &&
      (i.original || i.query).toLowerCase() !== query.toLowerCase()
    ));
    setCompareData((prev) => {
      const newData = { ...prev };
      delete newData[query.toLowerCase()];
      return newData;
    });
  }, []);

  const clearCompare = useCallback(() => {
    setCompareItems([]);
    setCompareData({});
  }, []);

  const isInCompare = useCallback((query) => {
    return compareItems.some((i) => 
      i.query.toLowerCase() === query.toLowerCase() ||
      (i.original || i.query).toLowerCase() === query.toLowerCase()
    );
  }, [compareItems]);
  
  // Phase 27: Get current entity type being compared
  const getCurrentEntityType = useCallback(() => {
    if (compareItems.length === 0) return null;
    return compareItems[0].entityType || null;
  }, [compareItems]);

  // Fetch data for all items in parallel
  const fetchCompareData = useCallback(async () => {
    if (compareItems.length === 0) return;

    setIsLoading(true);
    const results = {};

    try {
      const promises = compareItems.map(async (item) => {
        // Phase 93: Use search term (simplified) for API lookup
        const searchTerm = item.search || item.query;
        const key = (item.original || item.query).toLowerCase();
        
        // Use cached data if available
        if (compareData[key]) {
          results[key] = compareData[key];
          return;
        }

        // Fetch from API using the search term
        const token = localStorage.getItem("session_token");
        const response = await fetch(`${API}/search`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` })
          },
          body: JSON.stringify({ query: searchTerm }),
        });

        if (response.ok) {
          const data = await response.json();
          results[key] = data;
        }
      });

      await Promise.all(promises);
      setCompareData((prev) => ({ ...prev, ...results }));
    } catch (error) {
      console.error("Failed to fetch compare data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [compareItems, compareData]);

  const openCompareModal = useCallback(async () => {
    await fetchCompareData();
    setIsModalOpen(true);
  }, [fetchCompareData]);

  const closeCompareModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <CompareContext.Provider
      value={{
        compareItems,
        compareData,
        isModalOpen,
        isLoading,
        addToCompare,
        removeFromCompare,
        clearCompare,
        isInCompare,
        getCurrentEntityType,
        revertToOriginal,
        openCompareModal,
        closeCompareModal,
      }}
    >
      {children}
    </CompareContext.Provider>
  );
}

export function useCompare() {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error("useCompare must be used within a CompareProvider");
  }
  return context;
}
